import React, { useCallback, useEffect, useState } from 'react';

import { PencilFill as PencilIcon } from '@styled-icons/bootstrap/PencilFill';
import { Plus as PlusIcon } from '@styled-icons/bootstrap/Plus';
import { Save as SaveIcon } from '@styled-icons/boxicons-solid/Save';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';
import { DollarSign as DollarSignIcon } from '@styled-icons/fa-solid/DollarSign';
import { Refresh as RefreshIcon } from '@styled-icons/material-twotone/Refresh';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Text,
  Box,
  useMediaQuery,
  ModalFooter,
  HStack,
  Button,
  Icon,
  IconButton,
  ModalCloseButton,
  Input,
  InputGroup as ChakraInputGroup,
  InputRightAddon,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import SummaryContainer from 'components/SummaryContainer';
import SummaryDataChartCard from 'components/SummaryDataChartCard';

import useModal from 'hooks/useModal';

import { Budget } from 'types/budget';
import { InputGroup } from 'types/input';

import { updatePriceDetails } from './api';
import FragmentsTable, { FragmentSummary } from './FragmentsTable';
import PriceByGroupEditableTable from './PriceByGroupEditableTable';
import { PriceSpecificity } from './types';

type Props = ModalRootProps & {
  data: {
    id: number;
    budget_id: number;
    budget_bases: Budget['bases'];
    locale_id: number;
    locale_key: string;
    price_type_id: number;
    price_type_key: string;
    code: string;
    base: string;
    description: string;
    price: number;
    classification: string;
    type: string;
    unit_measure: string;
    compositionType?: 'common' | 'informed';
    priceSpecificities?: PriceSpecificity[];
  };
};

const modalMargin = 120;
const modalMobileFix = 100;

type LoadFragmentsFn = () => Promise<void>;
let loadFragmentsFn: LoadFragmentsFn;
type GroupSelection = { selectedGroup: InputGroup };
const noPendingPrice = null;

const ModalBudgetCompositionEdit: React.FC<Props> = ({
  data,
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const [description, setDescription] = useState<string>(data.description);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [priceSpecificities, setPriceSpecificities] = useState<
    PriceSpecificity[]
  >([]);
  const [pendingPriceReferenceId, setPendingPriceReferenceId] = useState<
    string | null
  >(noPendingPrice);
  const [isUpdatingPriceDetails, setIsUpdatingPriceDetails] = useState(false);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<FragmentSummary>(
    {} as FragmentSummary,
  );

  const { openModal } = useModal();

  const [isMobile] = useMediaQuery('(max-width: 728px)');
  const isInformedComposition = data.compositionType === 'informed';

  useEffect(() => {
    setPriceSpecificities(data.priceSpecificities || []);
    setPendingPriceReferenceId(noPendingPrice);
  }, [data.priceSpecificities]);

  const getPendingPriceReferenceId = useCallback(
    (specificities: PriceSpecificity[]): string | null => {
      const pendingSpecificity = specificities.find(
        (specificity) => specificity.total === null,
      );

      return pendingSpecificity?.referenceId || noPendingPrice;
    },
    [],
  );

  const hasInvalidSpecificities = useCallback(
    (specificities: PriceSpecificity[]): boolean =>
      specificities.some(
        (specificity) =>
          specificity.total === null ||
          Number.isNaN(specificity.total) ||
          specificity.total <= 0,
      ),
    [],
  );

  const toApiPriceSpecificities = useCallback(
    (specificities: PriceSpecificity[]): PriceSpecificity[] =>
      specificities.map((specificity) => ({
        ...specificity,
        total: Number(specificity.total),
      })),
    [],
  );

  const persistPriceDetails = useCallback(
    async (
      nextSpecificities: PriceSpecificity[],
      shouldUpdateDescription = false,
    ): Promise<void> => {
      if (!summary.id || !summary.composition_id) {
        return;
      }

      await updatePriceDetails({
        budgetId: data.budget_id,
        compositionId: summary.composition_id,
        priceId: summary.id,
        priceTypeId: summary.price_type_id,
        localeId: summary.locale_id,
        description,
        shouldUpdateDescription,
        currentCustomDescription: summary.custom_description ?? null,
        priceSpecificities: toApiPriceSpecificities(nextSpecificities),
      });
    },
    [data.budget_id, description, summary, toApiPriceSpecificities],
  );

  const getSummary = useCallback(async () => {
    setLoading(true);
    setSummary({} as FragmentSummary);

    try {
      const { data: response } = await api.get(
        `budget/${data.budget_id}/composition/${data.id}/price`,
        {
          params: {
            'filter[locale_id]': data.locale_id,
            'filter[price_type_id]': data.price_type_id,
          },
        },
      );

      const [summaryLoaded] = response.data;

      setSummary({
        id: summaryLoaded.id,
        custom_description: summaryLoaded.custom_description ?? null,
        total: summaryLoaded.total,
        material: summaryLoaded.material,
        material_percent: summaryLoaded.material_percent,
        equipment: summaryLoaded.equipment,
        equipment_percent: summaryLoaded.equipment_percent,
        labor: summaryLoaded.labor,
        labor_percent: summaryLoaded.labor_percent,
        third_party: summaryLoaded.third_party,
        third_party_percent: summaryLoaded.third_party_percent,
        other: summaryLoaded.other,
        other_percent: summaryLoaded.other_percent,
        price_type_id: summaryLoaded.price_type_id,
        locale_id: summaryLoaded.locale_id,
        composition_id: summaryLoaded.composition.id,
      });
    } catch {
      setSummary({} as FragmentSummary);
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  const registerLoadFragments = useCallback((fn: LoadFragmentsFn) => {
    loadFragmentsFn = fn;
  }, []);

  const handleStartEditingDescription = useCallback(() => {
    setIsEditingDescription(true);
  }, []);

  const handleSubmitNewDescription = useCallback(async () => {
    if (hasInvalidSpecificities(priceSpecificities)) {
      toast({
        description:
          'Preencha um preço maior que zero para o grupo de insumo antes de salvar a descrição.',
        status: 'warning',
      });
      return;
    }

    setIsUpdatingPriceDetails(true);

    try {
      setIsEditingDescription(false);

      await persistPriceDetails(priceSpecificities, true);
      await getSummary();

      toast({
        description: 'Descrição atualizada com sucesso.',
        status: 'success',
      });
    } catch (err) {
      setIsEditingDescription(false);
      setDescription(data.description);
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao atualizar o serviço.',
        status: 'error',
      });
    } finally {
      setIsUpdatingPriceDetails(false);
    }
  }, [
    data.description,
    getSummary,
    hasInvalidSpecificities,
    persistPriceDetails,
    priceSpecificities,
  ]);

  const handleConfirm = (): void => {
    if (onConfirm) {
      onConfirm();
    }

    handleClose();
  };

  const handleRefresh = useCallback(() => {
    if (!isInformedComposition && loadFragmentsFn) {
      loadFragmentsFn();
    }

    getSummary();
  }, [getSummary, isInformedComposition]);

  const handleAddGroups = useCallback(
    (groups: InputGroup[]): void => {
      if (pendingPriceReferenceId || isUpdatingPriceDetails) {
        return;
      }

      setPriceSpecificities((previousSpecificities) => {
        const existingReferences = new Set(
          previousSpecificities.map((item) => item.referenceId),
        );

        const groupsToAdd = groups.filter(
          (group) => group.key && !existingReferences.has(group.key),
        );

        if (!groupsToAdd.length) {
          return previousSpecificities;
        }

        const newSpecificities = groupsToAdd.map((group) => ({
          referenceId: group.key,
          description: group.description || '-',
          category: group.type || '-',
          total: null,
        }));

        setPendingPriceReferenceId(newSpecificities[0].referenceId);

        return [...previousSpecificities, ...newSpecificities];
      });
    },
    [isUpdatingPriceDetails, pendingPriceReferenceId],
  );

  const handleUpdateSpecificityPrice = useCallback(
    async (referenceId: string, total: number): Promise<void> => {
      if (
        Number.isNaN(total) ||
        total <= 0 ||
        (pendingPriceReferenceId && pendingPriceReferenceId !== referenceId)
      ) {
        if (total <= 0) {
          toast({
            description: 'O preço por grupo de insumo deve ser maior que zero.',
            status: 'warning',
          });
        }
        return;
      }

      const previousSpecificities = priceSpecificities;
      const nextSpecificities = previousSpecificities.map((specificity) =>
        specificity.referenceId === referenceId
          ? { ...specificity, total }
          : specificity,
      );

      const nextPendingRefId = getPendingPriceReferenceId(nextSpecificities);
      setPriceSpecificities(nextSpecificities);
      setPendingPriceReferenceId(nextPendingRefId);

      if (hasInvalidSpecificities(nextSpecificities)) {
        return;
      }

      setIsUpdatingPriceDetails(true);

      try {
        await persistPriceDetails(nextSpecificities);
        await getSummary();
      } catch (err) {
        setPriceSpecificities(previousSpecificities);
        setPendingPriceReferenceId(
          getPendingPriceReferenceId(previousSpecificities),
        );
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao atualizar os grupos de insumo.',
          status: 'error',
        });
      } finally {
        setIsUpdatingPriceDetails(false);
      }
    },
    [
      getPendingPriceReferenceId,
      getSummary,
      hasInvalidSpecificities,
      pendingPriceReferenceId,
      persistPriceDetails,
      priceSpecificities,
    ],
  );

  const handleRemoveSpecificity = useCallback(
    async (referenceId: string): Promise<void> => {
      if (pendingPriceReferenceId || isUpdatingPriceDetails) {
        return;
      }

      const previousSpecificities = priceSpecificities;
      const nextSpecificities = previousSpecificities.filter(
        (specificity) => specificity.referenceId !== referenceId,
      );

      setPriceSpecificities(nextSpecificities);
      setIsUpdatingPriceDetails(true);

      try {
        await persistPriceDetails(nextSpecificities);
        await getSummary();
      } catch (err) {
        setPriceSpecificities(previousSpecificities);
        setPendingPriceReferenceId(
          getPendingPriceReferenceId(previousSpecificities),
        );
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao atualizar os grupos de insumo.',
          status: 'error',
        });
      } finally {
        setIsUpdatingPriceDetails(false);
      }
    },
    [
      getPendingPriceReferenceId,
      getSummary,
      isUpdatingPriceDetails,
      pendingPriceReferenceId,
      persistPriceDetails,
      priceSpecificities,
    ],
  );

  return (
    <Modal {...restProps} scrollBehavior="inside" closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>
          {isInformedComposition
            ? 'Editar serviço a preço informado'
            : 'Editar serviço'}
        </ModalHeader>

        <ModalCloseButton onClick={handleConfirm} />

        <ModalBody>
          <SummaryContainer loading={loading}>
            <SummaryDataChartCard
              idx={1}
              isHidden={false}
              typeNumber="number"
              data={{
                icon: DollarSignIcon,
                title: 'VALOR TOTAL',
                number: summary.total || 0,
                formattedNumber: Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(summary.total || 0),
                colorSchemeCard: 'green',

                chart: {
                  colors: [
                    'rgb(237, 137, 54)',
                    'rgb(30, 108, 168)',
                    'rgb(33, 186, 69)',
                    'rgb(147,112,219)',
                    'rgb(251, 190, 8)',
                  ],
                  labels: [
                    `Material (${Intl.NumberFormat('pt-BR', {
                      maximumFractionDigits: 2,
                    }).format(summary.material_percent || 0)}%)`,
                    `Mão de Obra (${Intl.NumberFormat('pt-BR', {
                      maximumFractionDigits: 2,
                    }).format(summary.labor_percent || 0)}%)`,
                    `Equipamento (${Intl.NumberFormat('pt-BR', {
                      maximumFractionDigits: 2,
                    }).format(summary.equipment_percent || 0)}%)`,
                    `Serviços de terceiros (${Intl.NumberFormat('pt-BR', {
                      maximumFractionDigits: 2,
                    }).format(summary.third_party_percent || 0)}%)`,
                    `Outros (${Intl.NumberFormat('pt-BR', {
                      maximumFractionDigits: 2,
                    }).format(summary.other_percent || 0)}%)`,
                  ],
                  series: [
                    summary.material || 0,
                    summary.labor || 0,
                    summary.equipment || 0,
                    summary.third_party || 0,
                    summary.other || 0,
                  ],
                },
              }}
            />
          </SummaryContainer>

          <Flex
            width="100%"
            my={4}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width={{ base: '100%', md: '16%' }}
            >
              <Text fontWeight="600">Código</Text>
              <Text fontSize="smaller">
                {data.code} / {data.base}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Descrição</Text>

              {isEditingDescription ? (
                <ChakraInputGroup>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmitNewDescription();
                      }

                      if (e.key === 'Escape') {
                        setDescription(data.description);
                        setIsEditingDescription(false);
                      }
                    }}
                  />

                  <InputRightAddon px={0}>
                    <IconButton
                      height="100%"
                      colorScheme="blue"
                      aria-label="Salvar descrição"
                      title="Salvar descrição"
                      borderRadius="0"
                      onClick={handleSubmitNewDescription}
                      width="30px"
                    >
                      <Icon w={4} h={4} as={SaveIcon} />
                    </IconButton>

                    <IconButton
                      height="100%"
                      colorScheme="red"
                      aria-label="Cancelar"
                      title="Cancelar"
                      borderTopLeftRadius="0"
                      borderBottomLeftRadius="0"
                      onClick={() => {
                        setDescription(data.description);
                        setIsEditingDescription(false);
                      }}
                      width="30px"
                    >
                      <Icon w={4} h={4} as={CloseIcon} />
                    </IconButton>
                  </InputRightAddon>
                </ChakraInputGroup>
              ) : (
                <Flex
                  width="100%"
                  justifyContent="flex-start"
                  alignItems={{ base: 'flex-start', md: 'center' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <Text
                    fontSize="smaller"
                    fontStyle="italic"
                    _hover={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textColor: 'blue.500',
                    }}
                    onClick={handleStartEditingDescription}
                  >
                    {description}

                    <PencilIcon
                      size={10}
                      style={{ marginLeft: 10 }}
                      color="#0065FF"
                    />
                  </Text>
                </Flex>
              )}
            </Box>
          </Flex>

          <Flex
            width="100%"
            my={4}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Valor unitário</Text>

              <Text as="span" fontSize="smaller" fontStyle="italic">
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(summary.total || 0)}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Grupo de serviço</Text>
              <Text fontSize="smaller" fontStyle="italic">
                {data.classification}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Subgrupo de serviços</Text>
              <Text fontSize="smaller" fontStyle="italic">
                {data.type}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Unidade</Text>
              <Text fontSize="smaller" fontStyle="italic">
                {data.unit_measure}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600">Tipo de preço</Text>
              <Text fontSize="smaller" fontStyle="italic">
                {isInformedComposition ? 'Informado' : 'Composto'}
              </Text>
            </Box>
          </Flex>

          {isInformedComposition ? (
            <>
              <HStack my={5}>
                <Button
                  isLoading={loading || isUpdatingPriceDetails}
                  isDisabled={
                    loading ||
                    isUpdatingPriceDetails ||
                    !!pendingPriceReferenceId
                  }
                  colorScheme="green"
                  leftIcon={<Icon as={PlusIcon} />}
                  size="md"
                  onClick={() => {
                    openModal('compositionGroupsLookup', {
                      mode: 'lookup',
                      data: {
                        resourceType: 'input',
                        multiSelect: true,
                      },
                      onConfirm: (
                        groupsWithParent: GroupSelection | GroupSelection[],
                      ) => {
                        if (Array.isArray(groupsWithParent)) {
                          const groupsToAdd = groupsWithParent.map(
                            (selection) => selection.selectedGroup,
                          );

                          handleAddGroups(groupsToAdd);
                        } else {
                          handleAddGroups([groupsWithParent.selectedGroup]);
                        }
                      },
                    });
                  }}
                >
                  Adicionar grupo de insumo
                </Button>

                <IconButton
                  size="md"
                  colorScheme="blue"
                  title="Atualizar"
                  aria-label="Atualizar"
                  icon={<Icon as={RefreshIcon} w={5} h={5} />}
                  isLoading={loading || isUpdatingPriceDetails}
                  isDisabled={loading || isUpdatingPriceDetails}
                  onClick={handleRefresh}
                />
              </HStack>

              <PriceByGroupEditableTable
                data={priceSpecificities}
                onUpdatePrice={handleUpdateSpecificityPrice}
                onRemove={handleRemoveSpecificity}
                pendingPriceReferenceId={pendingPriceReferenceId}
                disableInteractions={
                  isUpdatingPriceDetails || !!pendingPriceReferenceId
                }
              />
            </>
          ) : (
            <>
              <HStack my={5}>
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  leftIcon={<Icon as={PlusIcon} />}
                  colorScheme="orange"
                  size="md"
                  onClick={() => {
                    openModal('budgetCompositionFragmentSelect', {
                      mode: 'select',
                      data: {
                        budgetId: data.budget_id,
                        compositionId: data.id,
                        bases: data.budget_bases,
                      },
                      onConfirm: () => {
                        loadFragmentsFn();
                        getSummary();
                      },
                    });
                  }}
                >
                  Adicionar composição
                </Button>

                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  colorScheme="green"
                  leftIcon={<Icon as={PlusIcon} />}
                  size="md"
                  onClick={() => {
                    openModal('budgetInputFragmentSelect', {
                      mode: 'select',
                      data: {
                        budgetId: data.budget_id,
                        compositionId: data.id,
                        bases: data.budget_bases,
                      },
                      onConfirm: () => {
                        loadFragmentsFn();
                        getSummary();
                      },
                    });
                  }}
                >
                  Adicionar insumo
                </Button>

                <IconButton
                  size="md"
                  colorScheme="blue"
                  title="Atualizar"
                  aria-label="Atualizar"
                  icon={<Icon as={RefreshIcon} w={5} h={5} />}
                  isLoading={loading}
                  isDisabled={loading}
                  onClick={handleRefresh}
                />
              </HStack>

              <FragmentsTable
                budget_id={data.budget_id}
                budget_bases={data.budget_bases}
                component_id={data.id}
                locale_id={data.locale_id}
                locale_key={data.locale_key}
                price_type_id={data.price_type_id}
                price_type_key={data.price_type_key}
                registerLoadFragments={registerLoadFragments}
                getSummary={getSummary}
                reloadTable={loadFragmentsFn}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button
              isLoading={loading}
              isDisabled={loading}
              colorScheme="green"
              size="md"
              onClick={handleConfirm}
            >
              Finalizar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetCompositionEdit;
